import React, { useState, useEffect } from "react";
import posthog from "posthog-js";
import Translation from "utils/Translation";
import { segmentHotelMapping } from "../utils/hotelConfig";
import { segmentToTag } from "../utils/TranslationUtils";

const EVENT_PREFIX = "luniapp_simulator_";
const isPosthogDebugEnabled = process.env.REACT_APP_POSTHOG_DEBUG === "true" || process.env.REACT_APP_ENV === "staging";

// Imports pour marques dont le fichier ne suit pas ${hotel}.png (Webpack)
/* eslint-disable global-require */
const delanoLogo = require("../img/simulator/delano.svg");
const hoxtonLogo = require("../img/simulator/hoxton.svg");
const theSebelLogo = require("../img/simulator/TheSebel.svg");
const mantisLogo = require("../img/simulator/Mantis.svg");
const logo21c = require("../img/simulator/21c-logo.png");
const logo25hours = require("../img/simulator/25hours.png");
const logoAdagioAccess = require("../img/simulator/adagio-access.jpg");
const logoAdagio = require("../img/simulator/Adagio.png");
const logoAdagioPremium = require("../img/simulator/AdagioPremium.png");
const logoArtSeries = require("../img/simulator/ArtSeries.png");
const logoBanyanTree = require("../img/simulator/banyan_tree.png");
const logoEmblems = require("../img/simulator/emblems.jpg");
const logoFaena = require("../img/simulator/faena.jpg");
const logoGleneagles = require("../img/simulator/gleneagles.png");
const logoHyde = require("../img/simulator/hyde.avif");
const logoMondrian = require("../img/simulator/mondrian.png");
const logoOrientExpress = require("../img/simulator/orient-express.webp");
const logoOurHabitas = require("../img/simulator/OUR-HABITAS.png");
const logoRedbury = require("../img/simulator/redbury.jpg");
const logoRixos = require("../img/simulator/rixos.png");
const logoSLS = require("../img/simulator/SLS.webp");
const logoSo = require("../img/simulator/SO.jpeg");
const logoSwissotel = require("../img/simulator/swissotel.png");
const logoTheOriginals = require("../img/simulator/the-originals.jpg");
/* eslint-enable global-require */

const hotelLogoOverrides = {
  "21C": logo21c,
  "25_Hours": logo25hours,
  Adagio_Access: logoAdagioAccess,
  Adagio_Original: logoAdagio,
  Adagio_Premium: logoAdagioPremium,
  Art_Series: logoArtSeries,
  Banyan_Tree: logoBanyanTree,
  Delano: delanoLogo.default || delanoLogo,
  Emblems: logoEmblems,
  Faena: logoFaena,
  Gleneagles: logoGleneagles,
  Hoxton: hoxtonLogo.default || hoxtonLogo,
  Hyde: logoHyde,
  Mantis: mantisLogo.default || mantisLogo,
  Mondrian: logoMondrian,
  Orient_Express: logoOrientExpress,
  Our_Habitas: logoOurHabitas,
  Redbury_Hotels: logoRedbury,
  Rixos: logoRixos,
  SLS: logoSLS,
  So: logoSo,
  Swissotel: logoSwissotel,
  The_Originals: logoTheOriginals,
  The_Sebel: theSebelLogo.default || theSebelLogo,
};

const BROCHURE_DOWNLOAD_URL =
  "https://drive.google.com/uc?export=download&id=1NgAYLK7i8lEhbHZp0wqQTgXovPs2s8Sd";

const SimulatorForm = ({ formData, setFormData, onSubmit }) => {
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!formData.segment) {
      setFormData((prev) => ({
        ...prev,
        segment: "Milieu de gamme",
        hotelType: segmentHotelMapping["Milieu de gamme"][0],
      }));
    }
  }, [formData, setFormData]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const getErrors = () => {
    const errors = {};
    if (!formData.numRooms || formData.numRooms <= 0) errors.numRooms = <Translation tag="form_error_numRooms" />;
    if (!formData.hotelType) errors.hotelType = <Translation tag="form_error_hotelType" />;
    if (!formData.occupationRate || formData.occupationRate <= 0) errors.occupationRate = <Translation tag="form_error_occupationRate" />;
    if (!formData.energyPrice || formData.energyPrice <= 0) errors.energyPrice = <Translation tag="form_error_energyPrice" />;
    if (!formData.waterPrice || formData.waterPrice <= 0) errors.waterPrice = <Translation tag="form_error_waterPrice" />;
    return errors;
  };

  const handleSubmit = () => {
    setSubmitted(true);
    const errors = getErrors();
    if (Object.keys(errors).length === 0) {
      onSubmit();
    }
  };

  return (
    <>
      <div className="simulator-info-card">
        <section className="simulator-info-header">
          <div className="simulator-info-text">
            <h1 className="simulator_P1_title">
              <Translation tag={"Simulator_P1_title"} />
            </h1>
            <p className="simulator_P1_description">
              <Translation tag={"Simulator_P1_description"} />
            </p>
          </div>
          <button
            type="button"
            className="simulator-header-button"
            onClick={() => {
              const eventName = `${EVENT_PREFIX}brochure_downloaded`;
              const payload = {
                source: "simulator_setup",
                source_app: "luniapp",
                app_scope: "simulator_accor",
              };
              posthog.capture(eventName, payload);
              if (isPosthogDebugEnabled) {
                console.info("[posthog] capture", eventName, payload);
              }
              window.open(BROCHURE_DOWNLOAD_URL, "_blank", "noopener,noreferrer");
            }}
          >
            <Translation tag="Simulator_P1_ViewBrochure" />
          </button>
        </section>
      </div>

      <div className="simulator-form-card">
        <div className="simulator_P1_form-infos">
          <div className="simulator_P1_form_elem-room simulator-form">
            {/* Nombre de chambres */}
            <div className={`simulator_P1_form_elem ${formData.segment ? "form-no-space" : "form-label-space"}`}>
              <label htmlFor="Nb_rooms">
                <Translation tag={"Simulator_Form_Rooms"} />
              </label>
              <input name="Nb_rooms" className="simulator-text-input" id="Nb_rooms" type="number" value={formData.numRooms} onChange={handleChange("numRooms")} />
              {submitted && getErrors().numRooms && <p className="form-error">{getErrors().numRooms}</p>}
            </div>

            {/* Taux d'occupation */}
            <div className={`simulator_P1_form_elem ${formData.segment ? "form-no-space" : "form-label-space"}`}>
              <label>
                <Translation tag={"Simulator_Form_occupationRate"} />
              </label>
              <div className="">
                <input name="occupationRate" className="simulator-text-input" id="occupationRate" type="number" value={formData.occupationRate} onChange={handleChange("occupationRate")} />
                <span>%</span>
                {submitted && getErrors().occupationRate && <p className="form-error">{getErrors().occupationRate}</p>}
              </div>
            </div>
          </div>

          {/* Type de marque */}
          <div className="simulator_P1_form_elem simulator-form simulator_P1_space">
            <label className="simulator_P1_Form_Stars">
              <Translation tag={"Simulator_P1_Form_Stars"} />
            </label>
            <div className="simulator_P1_stars-rating">
              {Object.keys(segmentHotelMapping).map((segment) => {
                const segmentKey = `segment_${segment.toLowerCase().replace(/\s|\/|-/g, "")}`;
                return (
                  <button
                    key={segment}
                    type="button"
                    className={`simulator_P1_star-button  ${formData.segment === segment ? "selected" : ""}`}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        segment,
                        hotelType: segmentHotelMapping[segment][0],
                      }))
                    }
                  >
                    <Translation tag={segmentKey} />
                  </button>
                );
              })}
              {submitted && getErrors().segment && <p className="form-error">{getErrors().segment}</p>}
            </div>
          </div>

          {formData.segment && (
            <div className={`simulator_P1_form_elem simulator_P1_space${formData.segment ? "form-no-space" : "form-label-space"} simulator-form`}>
              <label>
                <Translation
                  tag="Simulator_Form_SelectedBrand"
                  values={{
                    segment: <Translation tag={segmentToTag(formData.segment)} />,
                  }}
                />
              </label>
              <div className="hotel-images">
                {segmentHotelMapping[formData.segment].map((hotel) => {
                  let logoSrc = hotelLogoOverrides[hotel];
                  if (!logoSrc) {
                    try {
                      // Fallback sur l'ancien schéma en .png
                      logoSrc = require(`../img/simulator/${hotel}.png`);
                    } catch (e) {
                      logoSrc = null;
                    }
                  }

                  if (!logoSrc) {
                    return null;
                  }

                  return (
                    <div
                      key={hotel}
                      className={`hotel-image ${formData.hotelType === hotel ? "selected" : ""}`}
                      onClick={() => setFormData({ ...formData, hotelType: hotel })}
                    >
                      <img src={logoSrc} alt={hotel} className="hotel-logo" />
                    </div>
                  );
                })}
              </div>
              {submitted && getErrors().hotelType && <p className="form-error">{getErrors().hotelType}</p>}
            </div>
          )}

          {/* Prix de l'énergie et de l'eau */}
          <div className="simulator_P1_form_elem-price simulator-form">
            <div>
              <label>
                <Translation tag={"Simulator_Form_EnergyPrice"} />
              </label>
              <div className="">
                <input name="energyPrice" className="simulator-text-input" id="energyPrice" type="number" value={formData.energyPrice} onChange={handleChange("energyPrice")} />
                <span> € / kWh</span>
                {submitted && getErrors().energyPrice && <p className="form-error">{getErrors().energyPrice}</p>}
              </div>
            </div>

            <div className="">
              <label>
                <Translation tag={"Simulator_Form_WaterPrice"} />
              </label>

              <div className="">
                <input name="waterPrice" className="simulator-text-input" id="waterPrice" type="number" value={formData.waterPrice} onChange={handleChange("waterPrice")} />
                <span> € / m³</span>
                {submitted && getErrors().waterPrice && <p className="form-error">{getErrors().waterPrice}</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="simulator_P1_actions">
          <button type="button" className="simulator_P1_cta-button" onClick={handleSubmit}>
            <span className="simulator_P1_cta-label">
              <Translation tag="Simulator_Start" />
            </span>
          </button>
        </div>
      </div>
    </>
  );
};

export default SimulatorForm;
